import classNames from "classnames";
import { FC, ReactNode } from "react";
import { DefaultValues, FieldValues, Mode, UseFormReset, UseFormReturn, useForm } from "react-hook-form";
import { useI18n } from "../../hooks/useTranslations";
import { Entity } from "../../types";
import { formatTimeStamp } from "../../types/utils";
import Button from "./Button";
import styles from "./Form.module.css";
import LoadingSpinner from "./LoadingSpinner";
import SubmitBar from "./SubmitBar";

const EntityInfo: FC<{ info: Partial<Entity> }> = ({ info }) => {
  const i18n = useI18n();
  return (
    <div className={styles.edit}>
      {!!info.author && (
        <p>
          {i18n.t.common.createdBy} <strong>{info.author.fullName}</strong> {i18n.t.common.on}{" "}
          <strong>{formatTimeStamp(info.createdAt!, i18n.language)}</strong>
        </p>
      )}
      {!!info.editor && (
        <p>
          {i18n.t.common.editedBy} <strong>{info.editor.fullName}</strong> {i18n.t.common.on}{" "}
          <strong>{formatTimeStamp(info.modifiedAt!, i18n.language)}</strong>
        </p>
      )}
      {!!info.versionNumber && (
        <p>
          {i18n.t.common.version}: <strong>{info.versionNumber}</strong>
        </p>
      )}
    </div>
  );
};

export type FormChildrenProps<TFieldValues extends FieldValues> = UseFormReturn<TFieldValues> & {
  disabled?: boolean;
};

type Props<TFieldValues extends FieldValues> = {
  disabled?: boolean;
  secondaryButton?: ReactNode;
  onSubmit?: (reset: UseFormReset<TFieldValues>) => (data: TFieldValues) => Promise<void>;
  submitText?: string;
  defaultValues?: DefaultValues<TFieldValues>;
  children?: ReactNode | ((useFormProps: FormChildrenProps<TFieldValues>) => ReactNode);
  mode?: Mode;
  fullWidth?: boolean;
  itemCreationInfo?: Partial<Entity> | null;
};

const Form = <TFieldValues extends FieldValues>({
  children,
  disabled,
  secondaryButton,
  submitText,
  defaultValues,
  onSubmit,
  mode = "onSubmit",
  fullWidth = false,
  itemCreationInfo,
}: Props<TFieldValues>) => {
  const useFormProps = useForm<TFieldValues>({
    defaultValues,
    mode: mode,
  });

  const isDisabled = disabled || useFormProps.formState.disabled;

  return (
    <form
      className={classNames(styles.form, fullWidth ? styles["full-width"] : undefined)}
      onSubmit={(e) => {
        if (onSubmit) {
          e.stopPropagation();
          useFormProps.handleSubmit(onSubmit(useFormProps.reset))(e);
        }
      }}
    >
      {typeof children === "function" ? children({ ...useFormProps, disabled: isDisabled }) : children}

      {!!itemCreationInfo && <EntityInfo info={itemCreationInfo} />}
      {((onSubmit && submitText) || secondaryButton) && (
        <SubmitBar>
          {useFormProps.formState.isSubmitting && <LoadingSpinner />}
          {!!secondaryButton && secondaryButton}
          {onSubmit && submitText && (
            <Button
              buttonProps={{
                type: "submit",
                disabled: isDisabled || useFormProps.formState.isSubmitting,
              }}
            >
              {submitText}
            </Button>
          )}
        </SubmitBar>
      )}
    </form>
  );
};

export default Form;
